import { Box, Button, Center, Flex, Loader, Stack, Text } from '@mantine/core';
import axios from 'axios';
import { useApi } from 'api/api-context';
import BuildingSelectorMenu, { SelectableBuilding } from 'components/BuildingSelectorMenu';
import { HeaderIcon } from 'components/icons/HeaderIcon';
import useLang from 'lang';
import Login from 'pages/login/Login';
import React, { useEffect, useMemo, useState } from 'react';
import theme from 'theme';

type AccessState = 'idle' | 'loading' | 'ready' | 'error';

interface HelpdeskAccessGateProps {
  children: React.ReactNode;
}

interface BlockingStateProps {
  title: string;
  message: string;
  onRetry: () => void;
  onClearSession: () => void;
}

/**
 * Renders an application-level error that does not depend on a building layout.
 */
function BlockingState({ title, message, onRetry, onClearSession }: BlockingStateProps) {
  const { _t } = useLang();

  return (
    <Center mih="100vh" bg={theme.colors?.gray?.[1]} p="1rem">
      <Stack align="center" gap="lg" maw="28rem">
        <Box w="10rem" h="5rem">
          <HeaderIcon />
        </Box>
        <Stack align="center" gap="xs">
          <Text component="h1" size="xl" fw={600} ta="center" c={theme.colors?.gray?.[12]}>
            {title}
          </Text>
          <Text ta="center" c={theme.colors?.gray?.[9]}>
            {message}
          </Text>
        </Stack>
        <Flex gap="sm" wrap="wrap" justify="center">
          <Button variant="secondary" onClick={onClearSession}>
            {_t('Use another account')}
          </Button>
          <Button onClick={onRetry}>{_t('Try again')}</Button>
        </Flex>
      </Stack>
    </Center>
  );
}

/**
 * Prevents helpdesk routes from mounting until authentication and building
 * selection have both been validated.
 */
export default function HelpdeskAccessGate({ children }: HelpdeskAccessGateProps) {
  const { jwt, buildingId, getAction, selectBuilding, clearSession } = useApi();
  const { _t } = useLang();
  const [accessState, setAccessState] = useState<AccessState>('idle');
  const [buildings, setBuildings] = useState<SelectableBuilding[]>([]);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (!jwt) {
      setAccessState('idle');
      setBuildings([]);
      return;
    }

    let canceled = false;
    setAccessState('loading');

    const getCurrentUser = getAction('GetCurrentUser');
    getCurrentUser()
      .then((response) => {
        if (canceled) {
          return;
        }

        const selectableBuildings = (response.buildings || []).filter(
          (building): building is SelectableBuilding & { id: number } =>
            Number.isSafeInteger(building.id) && Number(building.id) > 0
        );

        setBuildings(selectableBuildings);
        setAccessState('ready');
      })
      .catch((error) => {
        if (canceled) {
          return;
        }

        if (axios.isAxiosError(error) && error.response?.status === 401) {
          clearSession();
          return;
        }

        setAccessState('error');
      });

    return () => {
      canceled = true;
    };
  }, [jwt, getAction, retryCount, clearSession]);

  const selectedBuildingIsValid = useMemo(
    () => buildingId !== null && buildings.some((building) => building.id === buildingId),
    [buildingId, buildings]
  );

  useEffect(() => {
    if (accessState !== 'ready' || selectedBuildingIsValid) {
      return;
    }

    if (buildings.length === 1) {
      selectBuilding(buildings[0].id!);
    } else if (buildingId !== null) {
      selectBuilding(null);
    }
  }, [accessState, buildingId, buildings, selectBuilding, selectedBuildingIsValid]);

  if (!jwt) {
    return <Login />;
  }

  if (accessState === 'idle' || accessState === 'loading') {
    return (
      <Center mih="100vh" bg={theme.colors?.gray?.[1]}>
        <Loader size="xl" aria-label={_t('Loading account')} />
      </Center>
    );
  }

  if (accessState === 'error') {
    return (
      <BlockingState
        title={_t('Unable to load your account')}
        message={_t('Check your connection and try again.')}
        onRetry={() => setRetryCount((count) => count + 1)}
        onClearSession={clearSession}
      />
    );
  }

  if (buildings.length === 0) {
    return (
      <BlockingState
        title={_t('No buildings available')}
        message={_t('Your account does not have access to a building. Please contact your administrator.')}
        onRetry={() => setRetryCount((count) => count + 1)}
        onClearSession={clearSession}
      />
    );
  }

  if (!selectedBuildingIsValid) {
    if (buildings.length === 1) {
      return (
        <Center mih="100vh" bg={theme.colors?.gray?.[1]}>
          <Loader size="xl" aria-label={_t('Selecting building')} />
        </Center>
      );
    }

    return (
      <Box mih="100vh" bg={theme.colors?.gray?.[1]}>
        <Center mih="100vh" p="1rem">
          <Stack align="center" gap="md">
            <Box w="10rem" h="5rem">
              <HeaderIcon />
            </Box>
            <Text c={theme.colors?.gray?.[9]} ta="center">
              {_t('Select a building to continue.')}
            </Text>
          </Stack>
        </Center>
        <BuildingSelectorMenu
          buildings={buildings}
          currentBuildingId={null}
          opened
          onClose={() => undefined}
          onSelect={selectBuilding}
          required
        />
      </Box>
    );
  }

  return <>{children}</>;
}
